import {
  AuditOutlined,
  BlockOutlined,
  CalculatorOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  UserOutlined
} from "@ant-design/icons"
import type React from "react"

export type AiCenterTabKey =
  | "receipt-financial-line"
  | "customer-sow-receivables"
  | "bid-from-sow"
  | "plans-3d-model"
  | "plans-sow"
  | "plans-estimate"

interface AiCenterTabDef {
  key: AiCenterTabKey
  /** The View right the tool's endpoints require (category.section.code). */
  viewPath: string
  labelKey: string
  tooltipKey: string
  status: "live" | "coming-soon"
  icon: React.ComponentType
}

// Order set by the product owner (2026-10-21; Estimates third and Scope of Work fourth since 2026-10-30): Full
// Project Execution is the tab the AI Center opens on. Bid from SOW and Estimates are
// live too; the rest show as Coming soon. Their sections stay in the codebase,
// unmounted, until each is released.
//
// The keys are the wire/link names and never change: `customer-sow-receivables` is
// Full Project Execution, so old `?tab=` links still open it.
export const AI_CENTER_TABS: readonly AiCenterTabDef[] = [
  {
    key: "customer-sow-receivables",
    viewPath: "ai_center.sow_receivables.sow_receivables_view",
    labelKey: "aiCenter.tabs.fullProjectExecution",
    tooltipKey: "aiCenter.tooltips.customerSowReceivables",
    status: "live",
    icon: UserOutlined
  },
  {
    key: "bid-from-sow",
    viewPath: "ai_center.bid_from_sow.bid_from_sow_view",
    labelKey: "aiCenter.tabs.bidFromSow",
    tooltipKey: "aiCenter.tooltips.bidFromSow",
    status: "live",
    icon: AuditOutlined
  },
  {
    key: "plans-estimate",
    viewPath: "ai_center.plans_estimate.plans_estimate_view",
    labelKey: "aiCenter.tabs.estimates",
    tooltipKey: "aiCenter.tooltips.estimates",
    status: "live",
    icon: CalculatorOutlined
  },
  {
    key: "plans-sow",
    viewPath: "ai_center.plans_sow.plans_sow_view",
    labelKey: "aiCenter.tabs.scopeOfWork",
    tooltipKey: "aiCenter.tooltips.scopeOfWork",
    status: "coming-soon",
    icon: FileTextOutlined
  },
  {
    key: "plans-3d-model",
    viewPath: "ai_center.plans_3d.plans_3d_view",
    labelKey: "aiCenter.tabs.visualizations3d",
    tooltipKey: "aiCenter.tooltips.visualizations3d",
    status: "coming-soon",
    icon: BlockOutlined
  },
  {
    key: "receipt-financial-line",
    viewPath: "ai_center.receipts.receipt_view",
    labelKey: "aiCenter.tabs.invoiceProcessing",
    tooltipKey: "aiCenter.tooltips.invoiceProcessing",
    status: "coming-soon",
    icon: FileDoneOutlined
  }
]

export const AI_CENTER_DEFAULT_TAB: AiCenterTabKey = AI_CENTER_TABS[0].key

// The URL parameter a section's open record lives in. The banner introduces a
// tool, so it shows on the tool's own list and not inside a record. Full Project
// Execution opens its plan over the page, so its list stays the page.
const OPEN_RECORD_PARAMS: Partial<Record<AiCenterTabKey, readonly string[]>> = {
  "bid-from-sow": ["bid"],
  "plans-estimate": ["estimate", "document"]
}

export const isRecordOpen = (
  key: AiCenterTabKey | null | undefined,
  params: URLSearchParams
) => Boolean(key && OPEN_RECORD_PARAMS[key]?.some((name) => params.get(name)))
